import React, { useMemo, useCallback } from 'react';
import { Box, Portal, ThemeProvider } from '@mui/material';
import { themeCreator } from './base';
import { StylesProvider } from '@mui/styles';
import { useSettingsContext } from 'src/core/settings/useSettings';

const ThemeProviderWrapper: React.FC = (props) => {
  const { settings, setSetting } = useSettingsContext();

  // Use settings.theme if set; otherwise fall back to old localStorage; otherwise default.
  // IMPORTANT: this does NOT write anything to settings until the user changes the theme.
  const themeName = settings?.theme || localStorage.getItem('appTheme') || DEFAULT_THEME;
  const theme = useMemo(() => themeCreator(themeName), [themeName]);
  const borderColor = getSystemBorderOption(settings?.SystemBorder).color;
  const borderThickness = getSystemBorderThickness(settings?.SystemBorderThickness);
  const setThemeName = useCallback((nextThemeName: string) => {
    setSetting('theme', nextThemeName);
    // Optional: keep this ONLY if you want backwards compatibility during rollout
    // localStorage.setItem('appTheme', nextThemeName);
  }, [setSetting]);
  const ctxValue = useMemo(() => ({ themeName, setThemeName }), [themeName, setThemeName]);

  return (
    <StylesProvider injectFirst>
      <ThemeContext.Provider value={ctxValue}>
        <ThemeProvider theme={theme}>
          {props.children}
          {borderColor && (
            <Portal>
              <Box aria-hidden="true" sx={{ position: 'fixed', inset: 0, border: `${borderThickness}px solid ${borderColor}`, boxSizing: 'border-box', pointerEvents: 'none', zIndex: 2147483647, displayPrint: 'none' }} />
            </Portal>
          )}
        </ThemeProvider>
      </ThemeContext.Provider>
    </StylesProvider>
  );
};

const DEFAULT_THEME = 'DarkTheme';

export const SYSTEM_BORDER_OPTIONS = [
  { value: 'none', label: 'No border', color: null },
  { value: 'live-red', label: 'Red', color: '#F44336' },
  { value: 'pre-live-blue', label: 'Blue', color: '#2196F3' },
  { value: 'stage-brown', label: 'Brown', color: '#8B4513' },
  { value: 'yellow', label: 'Yellow', color: '#FFEB3B' },
  { value: 'green', label: 'Green', color: '#00C853' },
  { value: 'black', label: 'Black', color: '#000000' },
  { value: 'white', label: 'White', color: '#FFFFFF' },
  { value: 'gray', label: 'Gray', color: '#9E9E9E' },
] as const;

// Missing, empty, null, or unrecognized values default to No border.
export const getSystemBorderOption = (value: unknown) => SYSTEM_BORDER_OPTIONS.find((option) => option.value === value) ?? SYSTEM_BORDER_OPTIONS[0];

// Numeric input values may arrive as strings; default to 6px and cap at 15px.
export const getSystemBorderThickness = (value: unknown) => {
  const thickness = typeof value === 'number' || typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(thickness) && thickness >= 1 ? Math.min(thickness, 15) : 6;
};

export const ThemeContext = React.createContext<ThemeContextValue>({ themeName: DEFAULT_THEME, setThemeName: () => { } });

export default ThemeProviderWrapper;

type ThemeContextValue = {
  themeName: string;
  setThemeName: (themeName: string) => void;
};
